// Shared activate mutation for chromium builds.
//
// Mirrors use-profile-release-mutations.ts in shape so the chromium
// PublishedBuildsTable can pin a build as `latestBuild` for its major
// with the same toast / invalidation contract the profile surface uses.
//
// Delete is intentionally out of scope here — chromium-side soft-delete
// has different confirm semantics and isn't part of the activate UX.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface ActivateResult {
	success?: boolean;
	[key: string]: unknown;
}

interface ApiError {
	error?: { message?: string };
}

interface ActivateArgs {
	buildId: string;
	// Displayed in the success toast so the operator immediately sees which
	// version just rolled. Optional so callers without the version handy
	// (e.g. legacy code paths) still work — the toast falls back to a
	// generic message.
	version?: string;
}

async function postActivate({
	buildId,
}: ActivateArgs): Promise<ActivateResult> {
	const res = await fetch(
		`/api/releases/chromium/${encodeURIComponent(buildId)}/activate`,
		{ method: "POST", credentials: "same-origin" },
	);
	const data = (await res.json()) as ActivateResult & ApiError;
	if (!res.ok) {
		throw new Error(data.error?.message ?? `activate ${res.status}`);
	}
	return data;
}

export function useChromiumBuildMutations(opts?: { onActivated?: () => void }) {
	const queryClient = useQueryClient();

	const activate = useMutation({
		mutationFn: postActivate,
		onSuccess: (_data, vars) => {
			toast.success(vars.version ? `Activated ${vars.version}` : "Activated");
			queryClient.invalidateQueries({ queryKey: ["chromium-builds"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
			opts?.onActivated?.();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Activate failed"),
	});

	return { activate };
}
